import PropTypes from 'prop-types';
import moment from 'moment';
import template from 'lodash/template';
import { logEvent } from 'api/audits/service';
import { EXCLUDE_FOR_PRINT, PAGE_LIMIT, GRID_CELL_RENDERER } from './basicGrid.constants';

export const limitPageCount = (locale) => (options) =>
  options
    .map((option) =>
      option.name === locale['common.all'] ? { name: option.value, value: option.value } : option
    )
    .filter((option) => option.name <= PAGE_LIMIT);

export const getPrintLoadingMsg = ({ allRecords, limit, locale }) => {
  let additional = '';
  if (allRecords > limit) {
    additional = ` ${template(locale['common.firstRecordsWillBePrinted'])({ limit })}`;
  }
  return locale['common.preparingForPrint'] + additional;
};

export const buildStaticViewColumn = ({ field, headerName, ...rest }) => {
  buildStaticViewColumn.propTypes = {
    field: PropTypes.string,
    headerName: PropTypes.string
  };

  const base = {
    aggFunc: null,
    filterable: true,
    hide: false,
    pinned: null,
    pivotIndex: null,
    rowGroupIndex: null,
    sortable: true
  };

  return {
    ...base,
    colId: field,
    field,
    headerName: headerName || '',
    ...rest
  };
};

export const defaultStringComparator = (value1 = '', value2 = '') =>
  value1?.toLowerCase()?.localeCompare(value2?.toLowerCase());

export const dateFilterParams = {
  comparator: /* istanbul ignore next */ (filterLocalDateAtMidnight, cellValue) => {
    const cellValueFormatted = moment(cellValue).format('YYYY-MM-DD');
    const filterLocalDateAtMidnightFormatted = moment(filterLocalDateAtMidnight).format(
      'YYYY-MM-DD'
    );
    return moment(cellValueFormatted).diff(moment(filterLocalDateAtMidnightFormatted));
  },
  inRangeInclusive: true
};

export const rangeFilterParams = {
  inRangeInclusive: true
};

export function normalizeNumber(number) {
  if (typeof number === 'number') {
    return number;
  }

  if (typeof number === 'string' && number) {
    return Number(number.replace(/[^0-9.]/g, ''));
  }

  // Ignore anything else
  return null;
}

export const sortColumnsByView = (cols, view) => {
  if (!view) {
    return cols;
  }

  const rest = cols.filter((x) => !view.includes(x.field));

  const ordered = view.map((v) => {
    const elem = cols.find((x) => x.field === v);
    const show = {
      ...elem,
      hide: false
    };
    return show;
  });

  const hidden = rest.map((h) => {
    const hide = {
      ...h,
      hide: true
    };
    return hide;
  });

  return [].concat.apply(ordered, hidden);
};

export const removeColumnsForPrint = (columnDefs) =>
  columnDefs.filter(
    ({ cellRenderer = '', disablePrint = false, field }) =>
      !EXCLUDE_FOR_PRINT.includes(field) &&
      !disablePrint &&
      cellRenderer !== GRID_CELL_RENDERER.ACTIONS
  );

export const logGridEvent = (eventName, { eventMessage, userName, widgetName }) => {
  if (!widgetName) return;
  logEvent({
    message: template(eventMessage)({ userName, widgetName }),
    subSvc: eventName
  });
};

export const loggedGridSync = (args, { locale, sync, widgetName }) => {
  if (!sync) return;
  logGridEvent(locale['common.widget'], { eventMessage: widgetName, widgetName });
  sync(...args);
};

// uses ag-grid gridApi to update selected rows and highlighting
// only fire selectionChanged event when last row is selected to increase performance
export const setGridRows = (
  notAllowedData,
  selectedAction,
  gridApi,
  actionsProperty = 'possibleActions'
) => {
  let count = 0;
  const allowedLength = notAllowedData.filter(
    (record) => record?.[actionsProperty]?.includes(selectedAction) && record
  ).length;

  const selectedNodes = gridApi.getSelectedNodes();

  gridApi.deselectAll();

  selectedNodes?.forEach((rowNode) => {
    if (rowNode?.data?.[actionsProperty]?.includes(selectedAction)) {
      // eslint-disable-next-line no-unused-expressions
      count < allowedLength ? rowNode.setSelected(true, false, true) : rowNode.setSelected(true);
      count += 1;
    } else {
      rowNode.setData({ ...rowNode.data, errorHighlighting: true });
    }
  });
};

export const resetGridErrorHighlighting = (gridApi) => {
  gridApi?.forEachNode((rowNode) => {
    if (rowNode?.data?.errorHighlighting) {
      const { errorHighlighting, ...rest } = rowNode?.data || {};
      rowNode.setData({ ...rest });
    }
  });
};

export const resetSavedViewsToFirstPage = (savedViews = []) => {
  return savedViews.map((savedView) => ({ ...savedView, page: 0 }));
};
